import { createFileRoute, redirect } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { type ChangeEvent, useCallback, useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import { useComposableFilter } from '@/components/ComposableFilter';
import Icon from '@/components/Icon/Icon';
import { HStack, VStack } from '@/components/Layout';
import { Text } from '@/components/Typography';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { CostOverviewFilterBar, type CostGroupBy, type CostOverviewViewMode, type ComparisonTarget } from '@/features/contract-and-cost/cost';
import { EditBottomSheet } from '@/features/contract-and-cost/EditBottomSheet';
import { METRIC_ROW_KEYS, useRowVisibility } from '@/features/contract-and-cost/grid';
import { getDefaultTargetMonth, getMonthsForTarget, isValidTargetMonth, parseTargetMonth, targetWindowStartIso } from '@/features/contract-and-cost/targetMonth';
import { CostStatCards } from '@/features/device/contract-and-cost/components/CostStatCards';
import { CostSummaryChart } from '@/features/device/contract-and-cost/components/CostSummaryChart';
import { MonthlyEditGrid, type MonthlyEditGridHandle } from '@/features/device/contract-and-cost/components/MonthlyEditGrid';
import { DeviceContractType } from '@/features/device/contract-and-cost/components/MonthlyEditGrid/types';
import { handleApiError } from '@/lib/api/error';
import { cn } from '@/lib/cn';
import { featureFlags } from '@/lib/env';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { DETAIL_OVERVIEW_ROW_KEYS, type DetailOverviewRowKey } from '../../-shared/detailOverviewRowVisibility';
import { CostOverviewTable } from './-components/CostOverviewTable';
import { DepartmentCostTable } from './-components/DepartmentCostTable';
import { MemberCostTable } from './-components/MemberCostTable';
import { useSaveDeviceCostMonthly } from './-mutations';
import { useDeviceCostByDepartmentForCatalog, useDeviceCostByMemberForCatalog, useDeviceCostCatalogDetail, useDeviceCostCatalogOverview } from './-query';
const SearchSchema = z.object({
  groupBy: z.enum(['catalog', 'member', 'department']).default('catalog'),
  viewMode: z.enum(['monthly', 'transition']).default('transition'),
  month: z.number().min(1).max(12).optional(),
  freeText: z.string().optional(),
  pageIndex: z.number().int().min(0).default(0),
  pageSize: z.number().int().min(1).max(1000).default(100),
  comparisonTarget: z.enum(['PREVIOUS_YEAR', 'PREVIOUS_MONTH']).default('PREVIOUS_MONTH')
});
export const Route = createFileRoute('/_staff/device-catalog/active/$id/_layout/contract-and-cost/_contract-and-cost/cost/$targetMonth/')({
  component: CostOverviewPage,
  validateSearch: zodValidator(SearchSchema),
  // 不正な対象月（YYYYMM 形式でない / 月が範囲外）は既定の対象月へリダイレクトする。
  beforeLoad: ({
    params
  }) => {
    if (!isValidTargetMonth(params.targetMonth)) {
      throw redirect({
        to: '/device-catalog/active/$id/contract-and-cost/cost/$targetMonth',
        params: {
          id: params.id,
          targetMonth: getDefaultTargetMonth()
        }
      });
    }
  }
});
function CostOverviewPage() {
  const {
    id,
    targetMonth
  } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const {
    t,
    i18n
  } = useTranslation(defaultNS, {
    keyPrefix: 'features.contractAndCost.costOverview'
  });
  // catalog 軸はドメインごとに名称が異なるため明示的に渡す（ソフトウェア =「ソフトウェア別」/ デバイス =「カタログ別」）。
  const {
    t: tGroupBy
  } = useTranslation(defaultNS, {
    keyPrefix: 'features.contractAndCost.overviewFilterBar.groupBy'
  });
  const {
    t: tCommon
  } = useTranslation(defaultNS, {
    keyPrefix: 'features.contractAndCost'
  });
  const {
    t: tCostTable
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.devices.contractAndCost.costOverview.table'
  });

  // CONTRACT_AND_COST_NEW_VIEW が無効な場合はカタログ別のみ提供する。
  // 個人別/組織別は非表示にし、URL 直アクセス時も catalog に強制する。
  const isNewViewEnabled = featureFlags.check('CONTRACT_AND_COST_NEW_VIEW');
  const groupBy = isNewViewEnabled ? search.groupBy : 'catalog';
  const groupByOptions: readonly CostGroupBy[] = isNewViewEnabled ? ['catalog', 'member', 'department'] : ['catalog'];
  const isCatalog = groupBy === 'catalog';
  const isMember = groupBy === 'member';
  const isDepartment = groupBy === 'department';
  const isMonthly = isCatalog && search.viewMode === 'monthly';
  const [editOpen, setEditOpen] = useState(false);
  const [costEditGridKey, setCostEditGridKey] = useState(0);
  const monthOptions = useMemo(() => getMonthsForTarget(targetMonth), [targetMonth]);

  // ページングは URL search params を単一の正とする（個人別ビューで使用）。
  const currentPage = search.pageIndex + 1;
  const {
    pageSize
  } = search;
  const {
    toast
  } = useToast();
  const costEditGridRef = useRef<MonthlyEditGridHandle>(null);
  const summaryRegionId = 'cost-overview-summary-region';
  const contracts = useMemo(() => Object.values(DeviceContractType).map(type => ({
    id: type,
    label: tCostTable(`contractType.${type}`)
  })), [tCostTable]);
  const saveCostMutation = useSaveDeviceCostMonthly(id, monthOptions, {
    onSuccess: () => {
      toast({
        title: tCommon('editBottomSheet.saveSuccessToast')
      });
      setEditOpen(false);
    },
    onError: error => {
      const {
        message
      } = handleApiError(error, i18n);
      toast({
        title: message,
        variant: 'destructive'
      });
    }
  });
  const currentMonth = search.month ?? new Date().getMonth() + 1;

  // 行表示トグル（実績 / 予定 / 予実差異 / 数量 / 平均単価 / 対象契約）。
  // 親に保持するため groupBy 切替時も表示状態を維持する。個人別 / 組織別ビューは指標 3 行のみ対象。
  const {
    visibility: rowVisibility,
    toggleRow: handleRowVisibilityToggle
  } = useRowVisibility(DETAIL_OVERVIEW_ROW_KEYS, METRIC_ROW_KEYS);
  const metricRowToggleItems: ReadonlyArray<{
    key: DetailOverviewRowKey;
    label: string;
  }> = [{
    key: 'actual',
    label: tCostTable('rowType.actual')
  }, {
    key: 'scheduled',
    label: tCostTable('rowType.scheduled')
  }, {
    key: 'variance',
    label: tCostTable('rowType.variance')
  }];
  const catalogRowToggleItems: ReadonlyArray<{
    key: DetailOverviewRowKey;
    label: string;
  }> = [...metricRowToggleItems, {
    key: 'quantity',
    label: tCostTable('rowType.quantity')
  }, {
    key: 'averageUnitPrice',
    label: tCostTable('rowType.averageUnitPrice')
  }, {
    key: 'targetContract',
    label: tCostTable('rowType.targetContract')
  }];
  const rowToggle = isMonthly ? undefined : {
    items: isCatalog ? catalogRowToggleItems : metricRowToggleItems,
    visibility: rowVisibility,
    onToggle: handleRowVisibilityToggle,
    requiredKeys: METRIC_ROW_KEYS
  };
  const [showSummary, setShowSummary] = useState(false);
  const filter = useComposableFilter<Record<string, never>>({
    filterDefs: [],
    filterState: {},
    onFilterStateChange: () => {}
  });

  // 対象月から派生する固定値。表示ウィンドウ先頭（対象月-4）を API のアンカーに渡す。
  const targetYearMonth = useMemo(() => targetWindowStartIso(targetMonth), [targetMonth]);
  // 選択中の対象月（テーブルの列ハイライト用）。
  const highlightMonth = useMemo(() => parseTargetMonth(targetMonth), [targetMonth]);
  const {
    data: summary
  } = useDeviceCostCatalogOverview(id, {
    targetYearMonth
  });
  const transitionPurchaseQuery = useDeviceCostCatalogDetail(id, {
    targetYearMonth,
    costComparisonTarget: search.comparisonTarget,
    contractType: 'PURCHASE'
  }, isCatalog);
  const transitionLeaseQuery = useDeviceCostCatalogDetail(id, {
    targetYearMonth,
    costComparisonTarget: search.comparisonTarget,
    contractType: 'LEASE'
  }, isCatalog);
  const memberQuery = useDeviceCostByMemberForCatalog(id, {
    startYearMonth: targetYearMonth,
    costComparisonTarget: search.comparisonTarget,
    durationMonths: monthOptions.length,
    page: currentPage,
    pageSize,
    freeText: search.freeText
  }, isMember);
  const departmentDrilldown = useDeviceCostByDepartmentForCatalog(id, {
    startYearMonth: targetYearMonth,
    costComparisonTarget: search.comparisonTarget,
    durationMonths: monthOptions.length
  }, {
    page: currentPage,
    pageSize
  }, isDepartment);
  const handleViewModeChange = useCallback((mode: CostOverviewViewMode) => {
    navigate({
      search: prev => ({
        ...prev,
        viewMode: mode,
        month: mode === 'monthly' ? currentMonth : undefined
      }),
      replace: true
    });
  }, [navigate, currentMonth]);
  const handleMonthChange = useCallback((month: number) => {
    navigate({
      search: prev => ({
        ...prev,
        month
      }),
      replace: true
    });
  }, [navigate]);
  const handleComparisonTargetChange = useCallback((target: ComparisonTarget) => {
    navigate({
      search: prev => ({
        ...prev,
        comparisonTarget: target
      })
    });
  }, [navigate]);
  const handleGroupByChange = useCallback((next: CostGroupBy) => {
    navigate({
      search: prev => ({
        ...prev,
        groupBy: next,
        // 個人別/組織別は推移のみ対応のため月選択をクリアする
        viewMode: next === 'catalog' ? prev.viewMode : 'transition',
        month: undefined,
        pageIndex: 0
      }),
      replace: true
    });
  }, [navigate]);
  const handlePageChange = useCallback((page: number) => {
    navigate({
      search: prev => ({
        ...prev,
        pageIndex: page - 1
      }),
      replace: true
    });
  }, [navigate]);
  const handlePageSizeChange = useCallback((size: number) => {
    navigate({
      search: prev => ({
        ...prev,
        pageIndex: 0,
        pageSize: size
      }),
      replace: true
    });
  }, [navigate]);
  const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    navigate({
      search: prev => ({
        ...prev,
        freeText: e.target.value || undefined,
        pageIndex: 0
      }),
      replace: true
    });
  }, [navigate]);

  // 個人別ビューのみフルテキスト検索を提供する（カタログ別/組織別は freeText を持たない）。
  const showSearch = isMember;
  return <div className="relative flex flex-1 flex-col gap-5 overflow-auto px-12 py-5 min-h-0">
      <div id={summaryRegionId} hidden={!showSummary}>
        {summary && <VStack className="gap-5">
            <CostStatCards summary={summary} />
            <CostSummaryChart data={summary.months} months={monthOptions} />
          </VStack>}
      </div>

      <div className="flex justify-end">
        <button type="button" className="flex items-center gap-1 transition-colors" aria-expanded={showSummary} aria-controls={summaryRegionId} onClick={() => setShowSummary(prev => !prev)}>
          <Icon iconName={showSummary ? 'ChevronUpIcon' : 'ChevronDownIcon'} className={cn('size-3.5')} />
          <Text className={cn('text-labelSmallReg', 'text-neutral-muted-on-fill', 'hover:text-neutral-on-fill')}>
            {showSummary ? t('summary.collapse') : t('summary.expand')}
          </Text>
        </button>
      </div>

      <CostOverviewFilterBar groupBy={groupBy} onGroupByChange={handleGroupByChange} groupByOptions={groupByOptions} groupByLabels={{
      catalog: tGroupBy('catalog')
    }} viewMode={search.viewMode} onViewModeChange={handleViewModeChange} selectedMonth={isMonthly ? currentMonth : undefined} onSelectedMonthChange={handleMonthChange} monthOptions={monthOptions} searchValue={showSearch ? search.freeText ?? '' : undefined} onSearchChange={showSearch ? handleSearchChange : undefined} filter={filter} comparisonTarget={search.comparisonTarget} onComparisonTargetChange={handleComparisonTargetChange} rowToggle={rowToggle} />

      {isCatalog && <>
          <HStack className="justify-end">
            <Button variant="outline" size="sm" className="gap-1.5 text-labelSmall" onClick={() => {
          setCostEditGridKey(k => k + 1);
          setEditOpen(true);
        }}>
              <Icon iconName="PencilIcon" className="size-3.5" />
              {tCommon('editButton')}
            </Button>
          </HStack>

          <EditBottomSheet open={editOpen} onOpenChange={setEditOpen} title={tCommon('editBottomSheet.editCostTitle')} onSave={() => {
        const rows = costEditGridRef.current?.getAllContractData();
        if (!rows?.length) return;
        saveCostMutation.mutate(rows);
      }} onCancel={() => setEditOpen(false)}>
            <MonthlyEditGrid key={costEditGridKey} ref={costEditGridRef} months={monthOptions} contracts={contracts} dataList={[transitionPurchaseQuery.data, transitionLeaseQuery.data].filter(data => data != null).map(data => ({
          contractType: data.contractType,
          months: data.months.map(month => ({
            targetYearMonth: month.targetYearMonth,
            plannedAmount: month.plannedCostAmount,
            actualAmount: month.actualCostAmount,
            quantity: month.quantity
          }))
        }))} />
          </EditBottomSheet>
        </>}

      {isMember && <MemberCostTable data={memberQuery.data} isPending={memberQuery.isPending} isError={memberQuery.isError} monthList={monthOptions} highlightMonth={highlightMonth} currentPage={currentPage} pageSize={pageSize} onPageChange={handlePageChange} onPageSizeChange={handlePageSizeChange} rowVisibility={rowVisibility} className="flex-1 min-h-0" />}

      {isDepartment && <DepartmentCostTable rootData={departmentDrilldown.rootData} visibleRows={departmentDrilldown.visibleRows} onToggle={departmentDrilldown.toggle} isPending={departmentDrilldown.isPending} isError={departmentDrilldown.isError} monthList={monthOptions} highlightMonth={highlightMonth} currentPage={currentPage} pageSize={pageSize} onPageChange={handlePageChange} onPageSizeChange={handlePageSizeChange} rowVisibility={rowVisibility} className="flex-1 min-h-0" />}

      {isCatalog && !isMonthly && <CostOverviewTable purchaseData={transitionPurchaseQuery.data} leaseData={transitionLeaseQuery.data} isPending={transitionPurchaseQuery.isPending || transitionLeaseQuery.isPending} months={monthOptions} highlightMonth={highlightMonth} rowVisibility={rowVisibility} className="flex-1 min-h-0" />}
    </div>;
}